<template>
    <!-- selection container -->
    <div class="max-mobile:flex-col max-mobile:flex-nowrap flex flex-wrap gap-4">
        <div class="flex flex-col gap-1">
            <div class="text-sm">{{ getStrByLng().LABEL_SELECT_SEASON }}</div>
            <Select
                v-model="selectedSeason"
                class="max-mobile:w-full"
                :empty-message="getStrByLng().MESSAGE_NO_SEASONS"
                :options="orderedSeasonData"
                :placeholder="getStrByLng().LABEL_SELECT_SEASON"
            />
        </div>
        <div v-if="!owlStore.qParams?.meeting" class="flex flex-col gap-1">
            <div class="text-sm">{{ getStrByLng().LABEL_SELECT_MEETING }}</div>
            <Select
                v-model="selectedMeeting"
                class="max-mobile:w-full"
                :empty-message="getStrByLng().MESSAGE_NO_MEETINGS"
                :option-label="(val: string) => val.replace('_', ' ')"
                :options="meetingsList"
                :placeholder="getStrByLng().LABEL_SELECT_MEETING"
                :pt="{
                    optionLabel: 'capitalize',
                    label: 'capitalize flex-auto overflow-hidden w-[1%] whitespace-nowrap text-ellipsis cursor-pointer block outline-hidden px-2 py-1 pe-6'
                }"
                show-clear
            />
        </div>
        <div class="flex flex-col gap-1">
            <div class="text-sm">{{ getStrByLng().LABEL_SELECT_DISCIPLINE }}</div>
            <WtcMultiSelect
                v-model="selectedDisciplines"
                class="flex w-72 cursor-pointer items-center border border-black p-2"
                :empty-message="getStrByLng().MESSAGE_NO_DISCIPLINES"
                expandable-options
                :max-display-options="3"
                :options="groupedDisciplinesSelectionList"
                :placeholder="getStrByLng().LABEL_ALL_DISCIPLINES"
                :pt="{
                    overlay: 'bg-white border border-black/50 mt-1 max-mobile:w-full x--theme-scrollbar',
                    dropdown: 'ml-2',
                    root: 'border-content inline-flex items-center justify-between border bg-white px-2 py-1 cursor-pointer select-none',
                    list: 'flex gap-6 text-sm p-3 py-2 pr-5 max-tablet:flex-col max-tablet:gap-2 max-tablet:px-2',
                    labelContainer: 'overflow-hidden',
                    label: 'overflow-hidden text-ellipsis whitespace-nowrap',
                    listContainer: 'max-laptop:!max-h-none !max-h-[26rem]',
                    optionGroup:
                        'border-l border-black/10 first:border-none pl-4 first:pl-0 py-1 max-laptop:pl-0 max-laptop:border-l-0 max-tablet:border-t max-tablet:pt-3 max-laptop:first:pt-1',
                    wtcList: '!grid-rows-10 max-laptop:grid-flow-row'
                }"
            >
                <template #group-icon="{ allSelected, someSelected }">
                    <img :src="allSelected ? checkActive : someSelected ? checkPartial : checkEmpty" />
                </template>
                <template #item-icon="{ isSelected }">
                    <img :src="isSelected ? checkActive : checkEmpty" />
                </template>
            </WtcMultiSelect>
        </div>
    </div>
    <!-- content -->
    <div class="mt-5">
        <WdlArchiveFetch
            :is-data-available="isMeetingDataAvailable"
            :is-fetching="isFetchingMeetingData"
            :place-holder-msg="meetingPlaceHolderMsg"
        >
            <!-- pdf-list -->
            <div class="text-brand-secondary max-mobile:grid max-mobile:grid-cols-2 mb-5 flex gap-4 text-sm">
                <WtcButton
                    v-if="meetingData?.PDFs.StartLists"
                    class="hover:underline"
                    :href="getPdfUrl(meetingData?.PDFs.StartLists)"
                    :icon="faFilePdf"
                    target="_blank"
                    >{{ getStrByLng().BTN_PDF_STARTLISTS }}</WtcButton
                >
                <WtcButton
                    v-if="meetingData?.PDFs.Results"
                    class="hover:underline"
                    :href="getPdfUrl(meetingData?.PDFs.Results)"
                    :icon="faFilePdf"
                    target="_blank"
                    >{{ getStrByLng().BTN_PDF_FULL_RESULTS }}</WtcButton
                >
                <WtcButton
                    v-if="meetingData?.PDFs.RecordSet"
                    class="hover:underline"
                    :href="getPdfUrl(meetingData?.PDFs.RecordSet)"
                    :icon="faFilePdf"
                    target="_blank"
                    >{{ getStrByLng().BTN_PDF_RECORD_SET }}</WtcButton
                >
                <WtcButton
                    v-if="meetingData?.PDFs.FlashInterview"
                    class="hover:underline"
                    :href="getPdfUrl(meetingData?.PDFs.FlashInterview)"
                    :icon="faFilePdf"
                    target="_blank"
                    >{{ getStrByLng().BTN_PDF_FLASH_QUOTES }}</WtcButton
                >
                <WtcButton
                    v-if="meetingData?.PDFs.Biographies"
                    class="hover:underline"
                    :href="getPdfUrl(meetingData?.PDFs.Biographies)"
                    :icon="faFilePdf"
                    target="_blank"
                    >{{ getStrByLng().BTN_PDF_BIOGRAPHIES }}</WtcButton
                >
            </div>
            <div class="flex flex-col border border-black/10">
                <WdlArchiveDisciplineItem
                    v-for="[key, eventData] in orderedDisciplineList"
                    :key="key"
                    :discipline="eventData"
                />
            </div>
        </WdlArchiveFetch>
    </div>
</template>

<script setup lang="ts">
import { faFilePdf } from "@fortawesome/free-solid-svg-icons";
import { useOwlStore, useTranslatedProperty } from "@swisstiming/owl-core";
import {
    getStrByLng,
    IWtcMultiSelectItem,
    IWtcMultiSelectOptions,
    WtcButton,
    WtcMultiSelect
} from "@swisstiming/webtec-kit";
import { AfterFetchContext, useFetch } from "@vueuse/core";
import { isEmpty, isNil, orderBy, toNumber } from "lodash-es";
import Select from "primevue/select";
import { computed, ref } from "vue";

import checkActive from "../assets/images/check-active.svg";
import checkEmpty from "../assets/images/check-empty.svg";
import checkPartial from "../assets/images/check-partial.svg";
import WdlArchiveDisciplineItem from "../components/WdlArchiveDisciplineItem.vue";
import WdlArchiveFetch from "../components/WdlArchiveFetch.vue";
import { useProvidSelections } from "../composables/provide.composables";
import { IInfo, IMeetingJson, IMeetingsJson } from "../interfaces/wdl-archive.interfaces";
import { useArchiveStore } from "../stores/wdl-archive.store";

const owlStore = useOwlStore();
const archiveStore = useArchiveStore();

const selectedMeeting = ref<string>();
const selectedDisciplines = ref<IWtcMultiSelectItem[]>([]);

const selectedSeason = ref<number>();
const queryParamSeasonAsNumber = computed(() =>
    archiveStore.qParams?.season ? toNumber(archiveStore.qParams?.season) : undefined
);

const { data: seasonsData } = useFetch(archiveStore.seasonsJsonUrl, {
    refetch: true,
    afterFetch(ctx: AfterFetchContext<number[]>) {
        // preselect latest season as default
        selectedSeason.value = queryParamSeasonAsNumber.value ?? ctx.data?.at(-1);
        return ctx;
    }
}).json<string[]>();

const orderedSeasonData = computed(() => orderBy(seasonsData.value, (s) => s, "desc"));

// if url-param is provided use param otherwise the selection
// 'meeting'-param will set results to only display the provided meeting and hide the selection
const relevantMeeting = computed(() => (owlStore.qParams?.meeting ?? selectedMeeting.value)?.toLowerCase());

// provide selection-information for use in child-components
useProvidSelections({
    selectedSeason,
    selectedMeeting: relevantMeeting
});

const meetingPlaceHolderMsg = computed(() => {
    if (isNil(owlStore.qParams?.meeting) && (!meetingsList.value || meetingsList.value?.length === 0)) {
        return getStrByLng().MESSAGE_NO_MEETINGS;
    }

    if (relevantMeeting.value && !isMeetingDataAvailable.value) {
        return getStrByLng().MESSAGE_NO_MEETING_RESULTS;
    }
    return "";
});

const getPdfUrl = (pdfFileName: string) =>
    archiveStore.getPdfUrl(selectedSeason.value, relevantMeeting.value, pdfFileName);

let defaultMeetingSetInitially = false;
// meetings.json
const { data: meetingsData } = useFetch(
    computed(() =>
        // dont request 'meetings.json' if a query-parameter already provides the meeting-id
        archiveStore.getMeetingsJsonUrl(!owlStore.qParams?.meeting ? selectedSeason.value : undefined)
    ),
    {
        refetch: true,
        updateDataOnError: true,
        afterFetch(ctx) {
            // 'default_meeting'-param will be interpreted once to display the provided meeting
            if (!defaultMeetingSetInitially) {
                selectedMeeting.value = archiveStore.qParams.default_meeting;
                defaultMeetingSetInitially = true;
            }
            return ctx;
        },
        onFetchError(ctx) {
            // reset selections if requested data is not available
            selectedMeeting.value = undefined;
            ctx.data = undefined;
            return ctx;
        }
    }
).json<IMeetingsJson>();

const meetingsList = computed(() => Object.values(meetingsData.value?.JsonNames ?? {}));

//MEETING_ID.json
const { data: meetingData, isFetching: isFetchingMeetingData } = useFetch(
    computed(() => archiveStore.getMeetingJsonUrl(selectedSeason.value, relevantMeeting.value)),
    {
        refetch: true,
        afterFetch(ctx: AfterFetchContext<IMeetingJson>) {
            selectedMeeting.value = relevantMeeting.value;

            // check if selected discipline is available in meeting
            // if not - reset discipline-selection
            const disciplineIds = Object.keys(ctx.data?.Data?.ResultData ?? []);
            const selectedDisciplineIds = selectedDisciplines.value?.map((discipline) => discipline.value);
            if (!disciplineIds.some((id) => selectedDisciplineIds?.includes(id))) {
                selectedDisciplines.value = [];
            }
            return ctx;
        },
        updateDataOnError: true,
        onFetchError(ctx) {
            selectedDisciplines.value = [];
            ctx.data = undefined;
            return ctx;
        },
        immediate: false
    }
).json<IMeetingJson>();
const isMeetingDataAvailable = computed(() => meetingData?.value?.Data?.ResultData !== undefined);

const menDisciplineList = computed(() =>
    Object.entries(meetingData.value?.Data?.ResultData ?? [])
        ?.filter(([key, eventData]) => eventData.Gender === "M")
        ?.map(([key, eventData]) => {
            return {
                value: key,
                label:
                    useTranslatedProperty<IInfo>(
                        Object.values(eventData.Data ?? {})?.at(0)?.Info,
                        "Event_Name",
                        "Event_Names"
                    ) ?? eventData.Name
            };
        })
);

const womenDisciplineList = computed(() =>
    Object.entries(meetingData.value?.Data?.ResultData ?? [])
        ?.filter(([key, eventData]) => eventData.Gender === "W")
        ?.map(([key, eventData]) => {
            return {
                value: key,
                label:
                    useTranslatedProperty<IInfo>(
                        Object.values(eventData.Data ?? {})?.at(0)?.Info,
                        "Event_Name",
                        "Event_Names"
                    ) ?? eventData.Name
            };
        })
);

const mixedAndOtherDisciplineList = computed(() =>
    Object.entries(meetingData.value?.Data?.ResultData ?? [])
        ?.filter(
            ([key, eventData]) => eventData.Gender === "X" || eventData.Gender === "" || eventData.Gender === undefined
        )
        ?.map(([key, eventData]) => {
            return {
                value: key,
                label:
                    useTranslatedProperty<IInfo>(
                        Object.values(eventData.Data ?? {})?.at(0)?.Info,
                        "Event_Name",
                        "Event_Names"
                    ) ?? eventData.Name
            };
        })
);

const groupedDisciplinesSelectionList = computed<IWtcMultiSelectOptions[]>(() => {
    if (relevantMeeting.value === undefined) {
        return [];
    }

    const list = [
        {
            label: getStrByLng().LABEL_ALL_MEN_DISCIPLINES,
            items: menDisciplineList.value?.map((discipline) => {
                return {
                    label: discipline.label,
                    value: discipline.value
                };
            })
        },
        {
            label: getStrByLng().LABEL_ALL_WOMEN_DISCIPLINES,
            items: womenDisciplineList.value?.map((discipline) => {
                return {
                    label: discipline.label,
                    value: discipline.value
                };
            })
        }
    ];

    if (mixedAndOtherDisciplineList.value.length > 0) {
        list.push({
            label: getStrByLng().LABEL_ALL_MIXED_DISCIPLINES,
            items: mixedAndOtherDisciplineList.value?.map((discipline) => {
                return {
                    label: discipline.label,
                    value: discipline.value
                };
            })
        });
    }

    return list;
});

const filteredDisciplineList = computed(() => {
    const selectedDisciplineKeys = selectedDisciplines.value.map((discipline) => discipline.value);
    return Object.entries(meetingData.value?.Data?.ResultData ?? []).filter(
        ([key, eventData]) => isEmpty(selectedDisciplines.value) || selectedDisciplineKeys.includes(key)
    );
});

const orderedDisciplineList = computed(() =>
    orderBy(filteredDisciplineList.value, ([key, eventData]) => eventData?.IsDiamondRace, "desc")
);
</script>

<style lang="scss"></style>
